import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { selectCanGoBack, useNavigationHistoryStore } from '@/store/useNavigationHistoryStore'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef } from 'react'

/** 戻る操作の完了イベントが来ないときに、次の押下を受け付け直すまでの時間 */
const PENDING_TIMEOUT_MS = 2000

/**
 * ヘッダーの「戻る」用。アプリ内に戻り先があればブラウザバック、なければ fallback へ replace する（#3665）。
 *
 * replace にするのは、直接開いた詳細ページを履歴に残さず、フォールバック先からの
 * ブラウザバックで詳細に戻ってしまわないようにするため。
 * 「戻り先があるか」は useTrackNavigationHistory が記録した Next.js 履歴キー列で判定する。
 */
export const useBackNavigation = () => {
  const router = useRouter()
  const { gen } = useCampaignSlug()
  // 戻る操作を始めた時刻。履歴ストアは遷移完了（routeChangeComplete）まで更新されないため、
  // 完了前の連打で router.back() が 2 回走り、2 エントリ戻ってアプリ外へ出るのを防ぐ
  const pendingSinceRef = useRef<number | null>(null)

  useEffect(() => {
    const clearPending = () => {
      pendingSinceRef.current = null
    }
    router.events.on('routeChangeComplete', clearPending)
    router.events.on('routeChangeError', clearPending)
    return () => {
      router.events.off('routeChangeComplete', clearPending)
      router.events.off('routeChangeError', clearPending)
    }
  }, [router.events])

  /**
   * @param fallbackPath 戻り先がないときの遷移先。省略時は HOME。
   *   onClick に直接渡すとイベントが第 1 引数に入るため、`() => goBack()` の形で呼ぶこと
   */
  const goBack = useCallback(
    (fallbackPath?: string) => {
      const now = Date.now()
      // 完了イベントが来ない経路（履歴マーカーへ戻った等）で押せなくならないよう、一定時間で受け付け直す
      if (pendingSinceRef.current !== null && now - pendingSinceRef.current < PENDING_TIMEOUT_MS) return
      pendingSinceRef.current = now

      // 押下時点の値をストアから直接読む。フックで購読すると、遷移直後に戻り先の有無が変わるたび
      // 戻るボタンを持つページ全体が再レンダーされるため
      if (selectCanGoBack(useNavigationHistoryStore.getState())) {
        router.back()
        return
      }
      void router.replace(fallbackPath ?? gen('/'))
    },
    [router, gen],
  )

  return { goBack }
}
